import type { RuntimeConfig } from "nuxt/schema";
import {
  type HygienaInstanceConfig,
  type HygienaToken,
  type HygienaEventMessage,
  HygienaAuthError,
  HygienaInstance,
} from "../utils/hygiena-instance";
import type { AuthFlow, AuthLoginOptions } from "../types";
import { getHygienaOptions } from "../utils/providers";
import BaseAuthClass from "../utils/base-auth-class";

export default class HygienaAuthProvider extends BaseAuthClass {
  #hygienaConfig: HygienaInstanceConfig;
  #hygienaInstance: HygienaInstance;
  #tokenPromise: Promise<HygienaToken | undefined> | undefined;
  #authFlow: AuthFlow;

  #getHygienaConfig(): HygienaInstanceConfig {
    return getHygienaOptions(this.config);
  }

  constructor(config: RuntimeConfig) {
    super(config);
    this.#hygienaConfig = this.#getHygienaConfig();
    this.#hygienaInstance = new HygienaInstance(this.#hygienaConfig);
    this.#hygienaInstance.addEventCallback((e: HygienaEventMessage) => this.#handleHygienaEvent(e));
    this.#authFlow = "idle";
    this.#tokenPromise = undefined;
  }

  get authFlow() {
    return this.#authFlow;
  }

  get authValidUntil() {
    if (!this.#hygienaToken?.expiresOn) return;

    return new Date(this.#hygienaToken.expiresOn).getTime() ?? undefined;
  }

  async resetAuthFlow() {
    const accounts = this.#hygienaInstance.getAllAccounts();
    this.#authFlow = !accounts.length ? "idle" : "authenticated";
  }

  async initialize() {
    const accounts = this.#hygienaInstance.getAllAccounts();
    if (accounts.length > 0) {
      await this.getToken();

      this.#authFlow = "authenticated";
      this.broadcastEvent({
        type: "auth-login-success",
        detail: {
          account: this.#castAccount(accounts[0]),
          token: this.#castToken(this.#hygienaToken),
        },
      });
    }
  }

  async login(options?: AuthLoginOptions) {
    const result = await this.#hygienaInstance
      .showLogin({
        redirectUri: this.#hygienaConfig.redirectUri,
        ...(options ?? {}),
      })
      .catch(async (err) => {
        if (err instanceof HygienaAuthError && err.cause instanceof CustomEvent && err.cause.type === "popup-closed") {
          return;
        }
        devConsole.warn(`🔒 ckAuth: Error during login: ${err.name} - ${err.message} - ${err.cause}`);
      });
    if (!result) return false;

    return this.#hygienaInstance.getAllAccounts().length > 0;
  }

  override async resetPassword(payload: { token: string; newPassword: string }) {
    try {
      return await this.#hygienaInstance.resetPassword(payload);
    } catch (e) {
      devConsole.warn("🔒 ckAuth: Error during password reset:", e);
      if (e instanceof HygienaAuthError) {
        throw new $ck.utils.UserFriendlyError(e.message);
      }
      throw e;
    }
  }

  async logout() {
    await this.#hygienaInstance.logout({
      postLogoutRedirectUri: "/",
    });
    await this.resetAuthFlow();
    return this.#authFlow === "idle";
  }

  async refreshToken(givenToken?: string) {
    if (givenToken) {
      await this.#refreshGivenToken(givenToken);
    } else {
      await this.#silentlyGetToken();
    }
    return this.#hygienaToken?.accessToken ?? "";
  }

  async getToken() {
    if (this.#tokenPromise) await this.#tokenPromise;
    if (this.isExpired()) {
      // this will either refresh the token silently or force a redirect through the login flow.
      await this.#silentlyGetToken();
    }

    return this.#hygienaToken?.accessToken ?? "";
  }

  async getAccounts() {
    return this.#hygienaInstance.getAllAccounts().map((el) => {
      return {
        username: el.username,
        name: el.displayName ?? "",
        id: el.username ?? "",
      };
    });
  }

  async isAuthed() {
    if (this.#tokenPromise) await this.#tokenPromise;
    return !this.isExpired();
  }

  async clearData() {
    await this.#hygienaInstance?.clearCache();
    await this.resetAuthFlow();
  }

  async handleAuthError(error: Error): Promise<boolean> {
    devConsole.warn("🔒 ckAuth: hygiena handleAuthError", error);
    return true;
  }

  get #hygienaToken() {
    return this.#hygienaInstance.activeToken;
  }

  #castAccount(account?: { username?: string; displayName?: string }) {
    return {
      username: account?.username ?? "",
      name: account?.displayName ?? "",
      id: account?.username ?? "",
    };
  }

  #castToken(token?: { accessToken?: string; expiresOn?: number | Date }) {
    return {
      token: token?.accessToken ?? "",
      expiresOn: token?.expiresOn ?? Date.now(),
    };
  }

  async #refreshGivenToken(token: string) {
    try {
      this.#tokenPromise = this.#hygienaInstance.setAccessToken(token);
      await this.#tokenPromise;
      this.#tokenPromise = undefined;
    } catch (e) {
      devConsole.warn("🔒 ckAuth: Error during silent given token refresh:", e);
      if (e instanceof HygienaAuthError) {
        throw new $ck.utils.UserFriendlyError(e.message);
      }
      throw e;
    }
  }

  async #silentlyGetToken() {
    try {
      this.#tokenPromise = this.#hygienaInstance.acquireTokenSilent();
      await this.#tokenPromise;
      this.#tokenPromise = undefined;
    } catch (e) {
      devConsole.warn("🔒 ckAuth: Error during silent token acquisition:", e);
      if (e instanceof HygienaAuthError) {
        throw new $ck.utils.UserFriendlyError(e.message);
      }
      throw e;
    }
  }

  #handleHygienaEvent(event: HygienaEventMessage) {
    switch (event.type) {
      case "auth-login-success":
      case "auth-token-refresh-success":
        this.#authFlow = event.type === "auth-login-success" ? "post-login" : this.#authFlow;
        this.broadcastEvent({
          type: event.type,
          detail: {
            account: this.#castAccount(event.detail.account),
            token: this.#castToken(event.detail.token),
          },
        });
        break;
      case "auth-logout":
        this.broadcastEvent({
          type: event.type,
        });
        break;
      case "auth-reset-password-success":
      case "auth-reset-password-failed":
      case "auth-busy-state-change":
      case "auth-token-refresh-failed":
        this.broadcastEvent(event);
        break;
    }
  }
}
